import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { refreshConnectorAuthQueries } from '#pplx/frontend/apps/ask/connectors/auth/refreshConnectorAuthQueries.js';
import { isInsufficientScopesError, useInsufficientScopesToast, } from '#pplx/frontend/apps/ask/connectors/auth/useInsufficientScopesToast.js';
import { useExternalOAuthOpener } from '#pplx/frontend/apps/ask/connectors/hooks/useExternalOAuthOpener.js';
import { OAuthIncompleteError, openRedirectUrl, } from '#pplx/frontend/apps/ask/oauth-redirect/openRedirectUrl.js';
import { getConnectorRedirectURL } from '#pplx/frontend/apps/ask/queries/mutations/connectorMutations.js';
import { sanitizedRedirectWindow } from '#pplx/frontend/common/sanitizedRedirectWindow.js';
import { PopupFailedToOpenError } from '#pplx/frontend/components/utils/popupUtils.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
export const useFileConnectorUrl = ({ reason, }) => {
    const searchParams = useSearchParams();
    const referrer = searchParams?.get('referrer');
    const referrerId = searchParams?.get('referrerId');
    const { $t } = useIntl();
    const { openToast } = useToastContext();
    const queryClient = useQueryClient();
    const openExternalUrl = useExternalOAuthOpener();
    const showInsufficientScopesToast = useInsufficientScopesToast();
    const handleConnect = useCallback(async ({ connectorName, redirectPath, customReferrer, customReferrerId, redirectOrigin, unauthedRedirectPath, openInNewTab = true, forceAccountSelection, retry, }) => {
        const redirectURL = await getConnectorRedirectURL({
            name: connectorName,
            referrer: customReferrer ?? referrer ?? undefined,
            referrerId: customReferrerId ?? referrerId ?? undefined,
            redirectPath: redirectPath ?? undefined,
            redirectOrigin,
            unauthedRedirectPath: unauthedRedirectPath ?? undefined,
            autoClose: openInNewTab || undefined,
            forceAccountSelection,
            reason,
        });
        if (!redirectURL) {
            openToast({
                message: $t({
                    defaultMessage: 'Unable to start connection.',
                    description: 'Toast error message when the file connector redirect URL is unavailable',
                    id: '24b1e2a397',
                }),
                variant: 'error',
                timeoutSeconds: 3,
            });
            return undefined;
        }
        try {
            const result = await openRedirectUrl({
                url: redirectURL,
                openInNewTab,
                openExternalUrl,
                // OAuth URLs contain state tokens and must not enter redirect logging.
                onRedirect: sanitizedRedirectWindow,
            });
            if (result.success !== true && openInNewTab) {
                throw new OAuthIncompleteError(result.errorCode);
            }
            // if opened in a new tab we need to invalidate the queries once the user has completed the connection in order to update the UI in the original tab
            if (openInNewTab) {
                await refreshConnectorAuthQueries(queryClient, connectorName);
            }
            return result;
        }
        catch (error) {
            if (isInsufficientScopesError(error)) {
                showInsufficientScopesToast({ connectorName, retry });
            }
            if (error instanceof PopupFailedToOpenError) {
                openToast({
                    message: $t({
                        defaultMessage: 'Please allow popups to connect your account.',
                        description: 'Toast error message shown when the OAuth popup window is blocked by the browser',
                        id: 'b9b88c5e37',
                    }),
                    variant: 'error',
                    timeoutSeconds: 3,
                });
                return undefined;
            }
            throw error;
        }
    }, [
        reason,
        referrer,
        referrerId,
        $t,
        openToast,
        queryClient,
        openExternalUrl,
        showInsufficientScopesToast,
    ]);
    return useMemo(() => ({ handleConnect }), [handleConnect]);
};
//# sourceMappingURL=useFileConnectorUrl.js.map